import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useCreateManualReservation } from '@/hooks/useCreateManualReservation'
import { ApiError, toApiError } from '@/lib/api/errors'
import { TIME_OF_DAY_OPTIONS } from '@/lib/venue/operatingHourTypes'
import {
  formatPhoneInput,
  hasManualCreateFormErrors,
  initialManualCreateFormState,
  MANUAL_PAYMENT_STATUS_OPTIONS,
  toManualCreateInput,
  validateManualCreateForm,
  type ManualCreateFormErrors,
} from '@/lib/venue/reservationManualCreateViewModel'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 전화·현장 예약 등록 모달(설계 §2, #675 §2-2) — 예약자 이름·연락처·일정·코트·결제 상태·메모(선택) 입력.
 *
 * 🚨 성공 응답이 곧 등록된 예약의 상세다 — 이 모달은 상세를 별도로 재조회하지 않는다. 🚨 격자 정렬·예약
 * 단위 배수·중복·휴무 등 서버 전용 규칙은 클라이언트에서 재검증하지 않고 서버 응답(400 메시지 / 409
 * `conflictReasonLabel`)을 그대로 보여준다(예약 변경 모달과 동일 원칙 — 중복 규칙 보유 금지).
 */
export function ReservationManualCreateModal({
  venueId,
  courts,
  defaultDate,
  onClose,
}: {
  venueId: string
  courts: { courtId: number; name: string }[]
  defaultDate: string
  onClose: () => void
}) {
  const [form, setForm] = useState(() =>
    initialManualCreateFormState({ useDate: defaultDate, courtId: courts[0]?.courtId })
  )
  const [errors, setErrors] = useState<ManualCreateFormErrors>({})
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  const createMutation = useCreateManualReservation(venueId)

  function updateField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit() {
    const validationErrors = validateManualCreateForm(form)
    if (hasManualCreateFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setGeneralError(null)

    try {
      await createMutation.mutateAsync(toManualCreateInput(form))
      onClose()
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  return (
    <Modal title="전화·현장 예약 등록" onClose={onClose}>
      <div className="space-y-4">
        <div className="space-y-1 rounded-lg bg-slateish-50 px-3 py-2 text-xs text-slateish-600">
          <p>이름·연락처·예약 일정·결제 상태만 기록합니다.</p>
          <p>등록된 예약자 정보는 연결된 구장 관리자만 조회할 수 있으며, 이용 완료 후 90일이 지나면 이름·연락처가 자동 삭제됩니다.</p>
          <p>다른 구장의 예약은 등록·조회할 수 없습니다.</p>
        </div>

        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
            {generalError.isRescheduleConflict && generalError.rescheduleConflict?.retriable ? (
              <p className="mt-1 text-xs">잠시 후 다시 시도해 주세요.</p>
            ) : null}
          </Alert>
        ) : null}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="manual-create-name">
            예약자 이름
          </label>
          <Input
            id="manual-create-name"
            value={form.bookerName}
            aria-invalid={Boolean(errors.bookerName)}
            placeholder="이름"
            onChange={(event) => {
              updateField('bookerName', event.target.value)
            }}
          />
          <FieldError message={errors.bookerName} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="manual-create-phone">
            연락처
          </label>
          <Input
            id="manual-create-phone"
            inputMode="numeric"
            value={form.bookerPhone}
            aria-invalid={Boolean(errors.bookerPhone)}
            placeholder="010-0000-0000"
            onChange={(event) => {
              updateField('bookerPhone', formatPhoneInput(event.target.value))
            }}
          />
          <FieldError message={errors.bookerPhone} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="manual-create-court">
            코트
          </label>
          <Select
            id="manual-create-court"
            value={form.courtId}
            aria-invalid={Boolean(errors.courtId)}
            onChange={(event) => {
              updateField('courtId', Number(event.target.value))
            }}
          >
            <option value={0} disabled>
              코트를 선택해 주세요
            </option>
            {courts.map((court) => (
              <option key={court.courtId} value={court.courtId}>
                {court.name}
              </option>
            ))}
          </Select>
          <FieldError message={errors.courtId} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="manual-create-date">
            날짜
          </label>
          <Input
            id="manual-create-date"
            type="date"
            value={form.useDate}
            aria-invalid={Boolean(errors.useDate)}
            onChange={(event) => {
              updateField('useDate', event.target.value)
            }}
          />
          <FieldError message={errors.useDate} />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-700">시간대</span>
          <div className="flex items-center gap-2">
            <Select
              aria-label="시작 시각"
              value={form.startTime}
              aria-invalid={Boolean(errors.time)}
              onChange={(event) => {
                updateField('startTime', event.target.value)
              }}
            >
              <option value="">시작 시각</option>
              {TIME_OF_DAY_OPTIONS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <span className="text-slateish-400">~</span>
            <Select
              aria-label="종료 시각"
              value={form.endTime}
              aria-invalid={Boolean(errors.time)}
              onChange={(event) => {
                updateField('endTime', event.target.value)
              }}
            >
              <option value="">종료 시각</option>
              {TIME_OF_DAY_OPTIONS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
          </div>
          <FieldError message={errors.time} />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-700">결제 상태</span>
          <div role="tablist" aria-label="결제 상태" className="flex gap-1 rounded-lg border border-slateish-300 p-1">
            {MANUAL_PAYMENT_STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={form.paymentStatus === option.value}
                onClick={() => {
                  updateField('paymentStatus', option.value)
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${
                  form.paymentStatus === option.value ? 'bg-brand-500 text-white' : 'text-slateish-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="manual-create-memo">
            메모
          </label>
          <Textarea
            id="manual-create-memo"
            rows={2}
            value={form.memo}
            placeholder="예) 라켓 대여 요청"
            aria-invalid={Boolean(errors.memo)}
            onChange={(event) => {
              updateField('memo', event.target.value)
            }}
          />
          <FieldError message={errors.memo} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            disabled={createMutation.isPending}
            onClick={() => {
              void handleSubmit()
            }}
          >
            {createMutation.isPending ? '등록 중…' : '등록'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
